import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { X } from 'lucide-react'
import { cms } from '@/lib/cms'
import { formatPrice } from '@/lib/format'
import { useBag } from '@/components/generals/bag-provider'
import { buttonClasses } from '@/components/generals/button'
import { MediaImage } from '@/components/generals/media-image'

export const BagNotice: FC = (): ReactNode => {
    const router = useRouter()
    const { notice, count, dismissNotice } = useBag()

    useEffect(() => {
        if (!notice) return

        const timer = window.setTimeout(dismissNotice, 8000)

        return () => window.clearTimeout(timer)
    }, [notice, dismissNotice])

    useEffect(() => {
        router.events.on('routeChangeStart', dismissNotice)

        return () => router.events.off('routeChangeStart', dismissNotice)
    }, [router.events, dismissNotice])

    return (
        <>
            <p role='status' className='sr-only'>
                {notice ? `${notice.name} was added to your bag.` : ''}
            </p>

            {notice && (
                <section
                    aria-labelledby='bag-notice-title'
                    className='fixed inset-x-4 top-20 z-50 border border-border bg-background p-5 shadow-[0_24px_60px_-20px_rgba(20,19,18,0.35)] animate-in fade-in slide-in-from-top-3 duration-300 sm:left-auto sm:right-6 sm:w-[380px] lg:top-32'
                >
                    <div className='flex items-start justify-between gap-4'>
                        <h2 id='bag-notice-title' className='eyebrow'>
                            Added to your bag
                        </h2>

                        <button type='button' onClick={dismissNotice} className='-m-2 p-2' aria-label='Close'>
                            <X className='size-4' strokeWidth={1.25} aria-hidden />
                        </button>
                    </div>

                    <div className='mt-5 flex gap-4'>
                        <div className='relative aspect-[3/4] w-20 shrink-0 overflow-hidden bg-muted'>
                            <MediaImage src={notice.image} alt={notice.imageAlt} sizes='80px' className='object-cover' />
                        </div>

                        <div className='min-w-0 text-sm'>
                            <p data-mdlfy-cms={cms('products', notice.productId, 'name')} className='font-heading text-xl leading-tight'>
                                {notice.name}
                            </p>

                            {notice.color && (
                                <p className='mt-2 text-muted-foreground'>
                                    Color: <span data-mdlfy-cms={cms('products', notice.productId, 'color')}>{notice.color}</span>
                                </p>
                            )}

                            {notice.size && (
                                <p className='mt-1 text-muted-foreground'>
                                    Size: {notice.size}
                                </p>
                            )}

                            <p className='mt-2'>
                                {formatPrice(notice.price)}
                            </p>
                        </div>
                    </div>

                    <div className='mt-5 grid grid-cols-2 gap-3'>
                        <button type='button' onClick={dismissNotice} className={buttonClasses('outline', 'h-11 px-4')}>
                            Continue
                        </button>

                        <Link href='/bag' className={buttonClasses('primary', 'h-11 px-4')}>
                            View bag ({count})
                        </Link>
                    </div>
                </section>
            )}
        </>
    )
}
